import { useSearchParams } from 'react-router-dom'

import {
  Box,
  Button,
  Card,
  EnhancedDimension,
  Tag,
  Text,
} from '@factorialco/deprecated-design-system'
import { PerformanceReviewOverviewPendingAgreementsTaskFragment } from 'generated/resources/graphql'

import Sidepanel from 'modules/core/components/Sidepanel'
import i18n from 'modules/core/lib/i18n'

import { MyAgreements } from 'modules/performance_process/components/overview/myAgreements'

interface Props {
  reviewProcess: PerformanceReviewOverviewPendingAgreementsTaskFragment
  minWidth?: EnhancedDimension
}

export const PendingAgreementsTask = ({ reviewProcess, minWidth }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const isSidepanelOpen = searchParams.get('myAgreementsPanel') === 'open'

  const targetsCount = reviewProcess.targets.totalCount ?? 0
  const isVisible =
    reviewProcess.agreementsConfiguration.enabled &&
    reviewProcess.reviewerStrategies?.includes('manager') &&
    targetsCount > 0

  if (!isVisible) return null

  const targetsWithoutAgreementCount = reviewProcess.targetsWithoutAgreement.totalCount ?? 0
  const targetsWithInitiableAgreementCount =
    reviewProcess.targetsWithInitiableAgreement.totalCount ?? 0
  const targetsWithSignedAgreementCount = reviewProcess.targetsWithSignedAgreement.totalCount ?? 0
  const targetsWithEsignedAgreementCount = reviewProcess.targetsWithEsignedAgreement.totalCount ?? 0

  const targetsWithFinishedAgreementCount =
    targetsWithSignedAgreementCount + targetsWithEsignedAgreementCount

  const isDisabled =
    targetsWithoutAgreementCount === targetsCount && targetsWithInitiableAgreementCount === 0
  const isCompleted =
    targetsWithSignedAgreementCount + targetsWithEsignedAgreementCount === targetsCount

  const pendingCount =
    targetsCount -
    targetsWithFinishedAgreementCount -
    targetsWithoutAgreementCount +
    targetsWithInitiableAgreementCount

  const onClick = () => setSearchParams({ myAgreementsPanel: 'open' })

  return (
    <Box minWidth={minWidth} width={minWidth}>
      <Card onClick={isDisabled ? undefined : onClick}>
        <Card.Title
          size="small"
          action={
            <>
              {!isCompleted && pendingCount > 0 && (
                <Tag leftItem="circle" inline capitalize type="tangerine">
                  {i18n.t('performance_process.overview.tasks.pending_tasks', {
                    count: pendingCount,
                  })}
                </Tag>
              )}
              {!isCompleted && isDisabled && (
                <Tag leftItem="circle" inline capitalize type="grey">
                  {i18n.t('performance_process.overview.tasks.agreements.not_available_yet')}
                </Tag>
              )}
            </>
          }
        >
          {i18n.t('performance_process.overview.tasks.agreements.title')}
        </Card.Title>
        <Card.Content>
          <Box gap="s16" width="full" flexGrow justifyContent="spaceBetween">
            <Text.Normal color="grey1000">
              {i18n.t('performance_process.overview.tasks.agreements.description')}
            </Text.Normal>
            {!isDisabled && (
              <Button onClick={onClick} type="secondary">
                {pendingCount === 0
                  ? i18n.t('performance_process.overview.tasks.view_all')
                  : i18n.t('performance_process.overview.tasks.agreements.action')}
              </Button>
            )}
          </Box>
        </Card.Content>
      </Card>

      <Sidepanel
        open={isSidepanelOpen}
        onClose={() => setSearchParams({})}
        width="wideXL"
        title={i18n.t('performance_process.agreements.my_agreements.title_new')}
      >
        <MyAgreements
          republishEvaluationsEnabled={
            reviewProcess.republishEvaluationsConfiguration?.enabled ?? false
          }
        />
      </Sidepanel>
    </Box>
  )
}
